import {
  calculateYearsBetween,
  formatDateAge,
} from '@airship-platform/airship-patterns';
import { FlexBox } from '@airship-platform/jam-web/components';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { InstalledEquipment } from '../../../../../app/data-access/appointmentEquipment';
import SystemTypeImage from '../../../../patterns/components/SystemTypeImage';
import SeeAllAssetsExistingSystemModal from '../ExistingSystems/SeeAllAssetsExistingSystemModal';
import SeeAllEquipmentsExistingSystemModal from '../ExistingSystems/SeeAllEquipmentsExistingSystemModal';
import SeeDetailsExistingSystemModal from '../ExistingSystems/SeeDetailsExistingSystemModal';

interface ExistingSystemProps {
  equipment: InstalledEquipment[];
  isLoading: boolean;
}

export interface InstalledEquipmentResponseExtended extends InstalledEquipment {
  age: string;
  capacityText: string;
  efficiencyText: string;
  warrantyText: string;
}

const ExistingSystem: React.FC<ExistingSystemProps> = ({
  equipment,
  isLoading,
}) => {
  const MAX_ELEMENTS = 3;
  const [isModalSeeDetailsOpen, setModalSeeDetailsOpen] = useState(false);
  const [isModalSeeAllAssetsOpen, setModalSeeAllAssetsOpen] = useState(false);
  const [isModalSeeAllEquipmentsOpen, setModalSeeAllEquipmentsOpen] =
    useState(false);
  const [selectedEquipment, setSelectedEquipment] =
    useState<InstalledEquipmentResponseExtended>();

  const handleModalSeeDetailsClose = () => {
    setModalSeeDetailsOpen(false);
  };

  const handleModalSeeAllAssetsClose = () => {
    setModalSeeAllAssetsOpen(false);
  };

  const handleModalSeeAllEquipmentsClose = () => {
    setModalSeeAllEquipmentsOpen(false);
  };

  const seeDetails = (equipment: InstalledEquipmentResponseExtended) => {
    setModalSeeDetailsOpen(true);
    setSelectedEquipment(equipment);
  };

  const getTextForMetric = (
    metric: InstalledEquipment['capacity'] | InstalledEquipment['efficiency']
  ) =>
    metric.map((m) => `${m.value.toLocaleString('en-US')} ${m.unit}`).join(',');

  const getTextForWarranty = (warranty: InstalledEquipment['warranty']) =>
    warranty.manufacturerStartDate && warranty.manufacturerEndDate
      ? calculateYearsBetween(
          warranty.manufacturerStartDate,
          warranty.manufacturerEndDate
        ) + ' years'
      : 'not determined';

  const sanitizedEquipment: InstalledEquipmentResponseExtended[] =
    equipment.map((e) => {
      return {
        ...e,
        age: e.installedOn ? formatDateAge(e.installedOn) : '',
        capacityText: getTextForMetric(e.capacity),
        efficiencyText: getTextForMetric(e.efficiency),
        warrantyText: getTextForWarranty(e.warranty),
        assets: e.assets.filter(
          (asset) => asset.type.toLocaleLowerCase() === 'image'
        ),
      };
    });

  if (isLoading) {
    return (
      <FlexBox alignItems="center" justifyContent="center">
        <CircularProgress color="primary" size="1.25rem" />
      </FlexBox>
    );
  }

  return !equipment.length ? (
    <FlexBox flexDirection="column" gap={3}>
      <Typography variant="h4" component="h2">
        Existing Equipment
      </Typography>
      <Typography>No installed equipment found</Typography>
    </FlexBox>
  ) : (
    <FlexBox flexDirection="column" gap={3}>
      {selectedEquipment && (
        <SeeDetailsExistingSystemModal
          open={isModalSeeDetailsOpen}
          equipment={selectedEquipment}
          onClose={handleModalSeeDetailsClose}
        />
      )}
      <SeeAllAssetsExistingSystemModal
        open={isModalSeeAllAssetsOpen}
        equipments={sanitizedEquipment}
        onClose={handleModalSeeAllAssetsClose}
      />
      <SeeAllEquipmentsExistingSystemModal
        open={isModalSeeAllEquipmentsOpen}
        equipments={sanitizedEquipment}
        onClose={handleModalSeeAllEquipmentsClose}
      />
      <Typography variant="h4" component="h2">
        Existing Equipment
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: ['1fr', '1fr 1fr 1fr', '1fr 1fr 1fr 1fr'],
          gap: 2,
          width: '100%',
        }}
      >
        {sanitizedEquipment.slice(0, MAX_ELEMENTS).map((equipment) => (
          <FlexBox
            key={equipment.locationEquipmentId}
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <SystemTypeImage systemTypeId={equipment.locationEquipmentId} />
            <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>
              {equipment.name}
            </Typography>
            <Typography variant="body3" sx={{ textAlign: 'center' }}>
              {[equipment.age, equipment.capacityText, equipment.efficiencyText]
                .filter((v) => v)
                .join(', ')}
            </Typography>
            <Typography component={'div'}>
              <Link
                onClick={() => seeDetails(equipment)}
                component="button"
                sx={{ marginLeft: 1, cursor: 'pointer' }}
              >
                See details
              </Link>
            </Typography>
          </FlexBox>
        ))}
        {sanitizedEquipment.length > MAX_ELEMENTS && (
          <FlexBox alignItems={'center'} justifyContent={'center'}>
            <Button
              color="softGold"
              size="small"
              onClick={() => setModalSeeAllEquipmentsOpen(true)}
            >
              See {sanitizedEquipment.length - MAX_ELEMENTS} more
            </Button>
          </FlexBox>
        )}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: ['1fr', '1fr 1fr 1fr', '1fr 1fr 1fr 1fr'],
          gap: 1,
        }}
      >
        {sanitizedEquipment
          .flatMap((system) => system.assets)
          .slice(0, MAX_ELEMENTS)
          .map((asset) => (
            <FlexBox
              key={asset.url}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <Box
                component="img"
                src={asset.url}
                alt={'Preview'}
                sx={{
                  width: '9.5rem',
                  height: '9rem',
                  objectFit: 'contain',
                }}
              />
            </FlexBox>
          ))}
        <FlexBox alignItems={'center'} justifyContent={'center'}>
          <Button
            color="softGold"
            size="small"
            onClick={() => setModalSeeAllAssetsOpen(true)}
          >
            See all photos
          </Button>
        </FlexBox>
      </Box>
    </FlexBox>
  );
};

export default ExistingSystem;
